<template>
  <div>
    <h3>{{ isRepeatCustomerRoute ? 'Repeated orders' : 'All' }} customers</h3>

    <div
      v-if="isTableLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>

    <template v-else>
      <!-- search input -->
      <div class="custom-search d-flex justify-content-between">
        <b-form-group>
            <b-form-input
              v-model="searchTerm"
              placeholder="Search"
              type="text"
              class="d-inline-block"
            />
        </b-form-group>
        <b-form-group v-if="canCreateCustomers">
          <b-button
              variant="primary"
              class="mx-1 float-right"
              size="sm"
              @click="openCustomerModal()"
          >
            <span>Create customer</span>
          </b-button>
        </b-form-group>
      </div>

      <vue-good-table
        mode="remote"
        :columns="columns"
        :rows="customers"
        :total-rows="customersTotal"
        :search-options="{
          enabled: false,
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
          perPageDropdownEnabled: false,
        }"
        @page-change="onCustomersPageChange"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img
              v-if="hasCustomerPicture(props.row.picture)"
              :src="pictureUrl(props.row.picture)"
              class="customer-table-picture"
              alt=""
            >
            <span
              v-else
              class="customer-table-picture customer-table-picture--empty"
              aria-hidden="true"
            />
          </span>
          <!-- Column: Bookings -->
          <span v-else-if="props.column.field === 'bookings'">
            {{ props.row.bookings.join(', ') }}
          </span>
          <span v-else-if="props.column.field === 'name'" class="customer-name-cell">
            <router-link :to="{ name: (companyPurpose === 'rent' ? 'bookings' : 'orders') + ('_by_user'), params: { customerId: props.row.id }}">
              <DatatableUserText :value="props.row.name" />
            </router-link>
            <span
              v-if="customerPrimaryCountryName(props.row)"
              class="customer-country-flag"
              v-b-tooltip.hover
              :title="customerPrimaryCountryName(props.row)"
            >
              {{ countryFlagEmoji(props.row.primary_address_country_short_name) }}
            </span>
          </span>
          <span v-else-if="props.column.field === 'created_at'">
            {{ formatCreatedAt(props.row.created_at) }}
          </span>
          <!-- Column: Action -->
          <span v-else-if="props.column.field === 'action'" class="action-link-group customers-action">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openEditModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>{{ canChangeCustomers ? 'View / Edit' : 'View' }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                :to="{ name: (companyPurpose === 'rent' ? 'bookings' : 'orders') + '_by_user', params: { customerId: props.row.id } }"
              >
                <feather-icon icon="BookIcon" class="mr-50" />
                <span>{{ companyPurpose === 'rent' ? 'Bookings' : 'Orders' }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click="openAddressesModal(props.row)">
                <feather-icon icon="MapPinIcon" class="mr-50" />
                <span>Addresses</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>

          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <!-- pagination -->
        <template #pagination-bottom="props">
          <TablePagination
              :value="tablePage"
              :total-rows="customersTotal"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; tablePage = 1; props.perPageChanged({ currentPerPage: n }); reloadCustomers() }"
            />
        </template>
      </vue-good-table>
    </template>
    <b-modal
        id="data-modal"
        :title="customerModalTitle"
    >
      <b-form>
        <template>

          <b-form-group
              label="Name"
              label-for="name"
          >
            <b-form-input
                id="name"
                v-model="editedCustomer.name"
                placeholder="Enter customer name"
            />
            <small class="text-danger" v-if="vCustomer$.editedCustomer.name.$error">Name is required</small>
          </b-form-group>

          <b-form-group
              label="Email"
              label-for="email"
          >
            <b-form-input
                id="email"
                v-model="editedCustomer.email"
                placeholder="Enter customer email"
                type="email"
                required
            />
            <small class="text-danger" v-if="vCustomer$.editedCustomer.email.$error">Required</small>
          </b-form-group>

          <b-form-group
              label="Phone number (optional)"
              label-for="phone"
          >
            <PhoneNumberInput
                id="phone"
                v-model:dial="customerDial"
                v-model:number="customerNumber"
            />
          </b-form-group>

          <b-form-group
              label="Card last 4 digits"
              label-for="card_last_4"
          >
            <b-form-input
                id="card_last_4"
                v-model="editedCustomer.card_last_4"
                placeholder="Enter card last 4 digits"
                type="number"
                :min="0"
                :max="9999"
            />
          </b-form-group>

          <b-form-group>
            <b-form-textarea
                v-model="editedCustomer.description"
                placeholder="Additional info"
                rows="3"
                max-rows="6"
            />
          </b-form-group>
        </template>
      </b-form>
      <template #modal-footer>
        <b-button
            variant="outline-secondary"
            class="float-right"
            @click="$bvModal.hide('data-modal')"
        >
          Close
        </b-button>
        <b-button
            variant="primary"
            class="float-right"
            @click="onSubmit"
            :disabled="isEditLoading"
        >
          <b-spinner v-if="isEditLoading" label="Spinning" small />
          <span v-else> Save </span>
        </b-button>
      </template>
    </b-modal>
    <Addresses
      :customer="customer"
      :click-date="clickDate"
      :allow-save="canChangeCustomers"
    />
    <ManageCustomer
      :clickDate="clickDateCustomer"
      :customer="manageCustomer"
    />
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  VBModal,
  BSpinner,
  BButton,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormDatepicker,
  BFormCheckbox,
  BFormTextarea, BRow, BCol, BImg,
} from 'bootstrap-vue'

import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import useVuelidate from '@vuelidate/core';
import { required, numeric, integer, minValue, minLength, email, helpers, requiredIf } from '@vuelidate/validators';
import {sweetError, sweetWarning} from "@/libs/sweet-alerts";
import Addresses from "./Components/Addresses.vue";
import ManageCustomer from "@/views/Components/ManageCustomer.vue";
import PhoneNumberInput from '@/views/Components/Elements/PhoneNumberInput.vue'
import {
  buildFullPhone,
  buildPhoneDialOptions,
  extractCountries,
  getDefaultDialCode,
  isValidFullPhone,
  parseFullPhone,
} from '@/libs/phoneNumber'
import { canStaffCreate, canStaffChange } from '@/libs/staffPermissions'
import { countryFlagEmoji, hasCustomUserPicture, normalizeImageUrl } from '@/libs/utils'
import { formatCompanyDateTime } from '@/libs/companyTime'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'

export default {
  components: {
    TablePagination,
    ManageCustomer,
    PhoneNumberInput,
    DatatableCellValue,
    DatatableUserText,
    BCol, BRow,
    Addresses,
    BFormTextarea,
    BFormCheckbox,
    BFormDatepicker,
    BForm,
    BDropdown,
    BDropdownItem,
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BSpinner,
    BButton,
    BImg,
  },
  directives: {
    'b-modal': VBModal,
  },
  data() {
    return {
      companyPurpose: window.companyPurpose,
      clickDate: null,
      clickDateCustomer: null,
      manageCustomer: null,
      modalVisible: true,
      customer: null,
      isEditLoading: false,
      customerDial: String(window.companyPhoneCode || '1').replace(/\D/g, '') || '1',
      customerNumber: '',
      editedCustomer: {
        id: null,
        name: null,
        email: null,
        phone: null,
        description: null,
        card_last_4: null,
      },
      required,
      email,
      isTableLoading: true,
      searchTerm: '',
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          sortable: false,
          width: '70px',
          thClass: 'customer-picture-col',
          tdClass: 'customer-picture-col',
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Phone',
          field: 'phone',
        },
        {
          label: 'Email',
          field: 'email',
        },
        {
          label: 'Created at',
          field: 'created_at',
          sortable: false,
        },
        /*{
          label: 'Stripe Id',
          field: 'stripe_id',
        },*/
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center customers-action-col',
          tdClass: 'text-center customers-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
      tablePage: 1,
      searchTimer: null,
      suppressSearchReload: false,
    }
  },
  computed: {
    ...mapGetters({
      customers: 'customers/customers',
      customersTotal: 'customers/customersTotal',
      authUser: 'auth/user',
    }),
    isAdmin() {
      return this.authUser?.role === 'Admin'
    },
    canCreateCustomers() {
      return canStaffCreate(this.authUser, 'customers')
    },
    canChangeCustomers() {
      return canStaffChange(this.authUser, 'customers')
    },
    customerModalTitle() {
      const dbSuffix = this.isAdmin && this.editedCustomer.internal_id
        ? ` (DB #${this.editedCustomer.internal_id})`
        : ''
      return `Edit customer details #${this.editedCustomer.id}${dbSuffix}`
    },
    isRepeatCustomerRoute() {
      // This will check if the current route is '/admin/customers/repeat'
      return this.$route.path === '/admin/customers/repeat-order-customers';
    }
  },
  watch: {
    searchTerm() {
      if (this.suppressSearchReload) {
        return
      }
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.tablePage = 1
        this.reloadCustomers()
      }, 300)
    },
    '$route.path'(newPath, oldPath) {
      if (!oldPath || newPath === oldPath) {
        return
      }
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.suppressSearchReload = true
      this.tablePage = 1
      this.searchTerm = ''
      this.$nextTick(() => {
        this.suppressSearchReload = false
        this.reloadCustomers()
      })
    },
  },
  async mounted() {
    await this.reloadCustomers()
  },
  validations() {

    return {
      editedCustomer: {
        name: { required },
        email: { required, email }
      }
    };
  },
  setup() {
    return { vCustomer$: useVuelidate() };
  },
  methods: {
    ...mapActions({
      loadCustomers: 'customers/loadCustomers',
      loadRepeatCustomers: 'customers/loadRepeatCustomers',
      updateCustomer: 'customers/updateCustomer',
      recovery: 'customers/recovery',
    }),
    buildCustomerFilters() {
      const filters = {
        page: this.tablePage,
        per_page: this.pageLength,
      }
      const keyword = String(this.searchTerm || '').trim()
      if (keyword !== '') {
        filters.keyword = keyword
      }
      return filters
    },
    onCustomersPageChange(params) {
      this.tablePage = params.currentPage
      this.reloadCustomers()
    },
    reloadCustomers() {
      this.isTableLoading = true
      const loader = this.isRepeatCustomerRoute
        ? this.loadRepeatCustomers
        : this.loadCustomers

      return loader(this.buildCustomerFilters())
        .then((result) => {
          const total = Number(result?.total ?? 0)
          const maxPage = Math.max(1, Math.ceil(total / this.pageLength) || 1)
          if (this.tablePage > maxPage) {
            this.tablePage = maxPage
            return this.reloadCustomers()
          }
          return result
        })
        .catch(() => {
          toastFailure('Something went wrong.')
        })
        .finally(() => {
          this.isTableLoading = false
        })
    },
    showModal() {
      this.modalVisible = true;
    },
    showError(responseData=null, warning=false) {
      if (responseData != null && responseData.message != undefined && responseData.message != null) {
        let errorMSG = responseData.message + '</br>';
        if(responseData.errors){
          Object.keys(responseData.errors).map(function(errorKey){
            responseData.errors[errorKey].map(function(value, key){
              errorMSG += value + '</br>';
            });
          });
        }
        if( warning ){
          sweetWarning('Warning', errorMSG);
        }
        else{
          sweetError('Error', errorMSG);
        }
      }
      else{
        toastFailure("Something went wrong while saving the data");
      }
    },
    openAddressesModal(row) {
      this.customer = row;
      this.clickDate = new Date();
    },
    openCustomerModal() {
      this.manageCustomer = null;
      this.clickDateCustomer = new Date();
    },
    async openEditModal(row) {
      this.manageCustomer = {
        id: row.id,
        created_at: row.created_at || null,
      };
      this.clickDateCustomer = new Date();
    },
    pictureUrl(picture) {
      return normalizeImageUrl(picture)
    },
    hasCustomerPicture(picture) {
      return hasCustomUserPicture(picture)
    },
    countryFlagEmoji,
    formatCreatedAt(value) {
      return formatCompanyDateTime(value) || ''
    },
    customerPrimaryCountryName(row) {
      return row?.primary_address_country || null
    },
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      });
    },
    async sendPasswordReset(email){

      if (await this.confirmationMessage('Are you sure you want to send password recovery email to '+email+'?')) {
        await this.recovery({email: email,})
          .then(() => {
            toastSuccess('Recovery link has been send.')
          })
          .catch(() => {
            toastFailure('The given data was invalid.')
          })
          .finally(() => {
            this.isRecoveyLoading = false
          })
      }
    },
    getValidationErrors() {
      const fieldLabels = {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
      };

      const errors = this.vCustomer$.editedCustomer.$errors.map((error) => {
        const fieldName = fieldLabels[error.$property] || 'Field';

        if (error.$validator === 'required') {
          return `${fieldName} is required.`;
        }
        if (error.$validator === 'email') {
          return `${fieldName} must be a valid email address.`;
        }
        return `${fieldName} is invalid.`;
      });

      return [...new Set(errors)];
    },
    async onSubmit(event) {

      event.preventDefault();

      this.editedCustomer.phone = String(this.customerNumber || '').trim()
        ? buildFullPhone(this.customerDial, this.customerNumber)
        : null;

      const isFormCorrect = await this.vCustomer$.editedCustomer.$validate();
      if (!isFormCorrect) {
        const errors = this.getValidationErrors();
        if (errors.length) {
          sweetError('Validation error', errors.join('</br>'));
        }

        return;
      }

      if (String(this.customerNumber || '').trim() && !isValidFullPhone(this.editedCustomer.phone)) {
        sweetError('Validation error', 'Please enter a valid phone number.');
        return;
      }

      this.submitForm();
    },
    submitForm() {

      this.isEditLoading = true;
      this.updateCustomer(this.editedCustomer)
          .then((response) => {
            this.$root.$emit("bv::hide::modal", "data-modal");
            toastSuccess('Customer updated');
          })
          .catch((error) => {
            //this.showError(error.response.data);
            // @todo let's use some universal function for all error handlings
            const responseData = error && error.response && error.response.data ? error.response.data : null;
            if (responseData) {
              let errorMSG = (responseData.message || 'Failed.') + '</br>';
              //if (responseData.errors) {
                Object.keys(responseData).map((errorKey) => {
                  responseData[errorKey].map((value) => {
                    errorMSG += value + '</br>';
                  });
                });
              //}

              sweetError('Error', errorMSG);
            }
          })
          .finally(() => {
            this.isEditLoading = false
          })
    }
  },
}
</script>

<style scoped>
.customers-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

.customer-picture-col {
  width: 56px !important;
  min-width: 56px !important;
  max-width: 56px !important;
  padding-left: 0.35rem !important;
  padding-right: 0.35rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

.customer-table-picture {
  display: inline-block;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  vertical-align: middle;
}

.customer-table-picture--empty {
  background: #e0e0e0;
  border: 1px solid #d0d0d0;
}

.customer-name-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
}

.customer-country-flag {
  font-size: 1.15em;
  line-height: 1;
  cursor: default;
}

.customers-action {
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 auto;
}

.customers-action :deep(.dropdown),
.customers-action :deep(.btn-group) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  float: none;
}

.customers-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
</style>